import type { EnumOption } from '@mss_creator/shared';
import { OptionTile } from '@pms-digital/design-system';
import { type ReactNode, useId } from 'react';

interface OptionTilesProps {
  label: string;
  options: readonly EnumOption[];
  value: string;
  /** The registry default, marked « défaut » on its tile; omit for no marker. */
  defaultValue?: string;
  /** Builds the miniature of one option; omit for plain tiles. */
  renderPreview?: (optionValue: string) => ReactNode;
  disabled?: boolean;
  onChange: (value: string) => void;
}

/**
 * The options of a setting as a radio group of the package's `OptionTile`
 * (real `<input type="radio">`, arrow keys from the shared `name`). Each tile
 * carries its miniature under the label, when the setting has one. Two per row
 * on a phone (a grid, never a horizontal scroll), a wrapping row from `sm` up.
 */
export function OptionTiles({
  label,
  options,
  value,
  defaultValue,
  renderPreview,
  disabled = false,
  onChange,
}: OptionTilesProps) {
  const name = useId();
  let tileWidth = '';
  if (renderPreview) tileWidth = options.length > 4 ? 'sm:w-[148px]' : 'sm:w-[196px]';
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3"
    >
      {options.map((option) => (
        <div key={option.value} className={tileWidth}>
          <OptionTile
            type="radio"
            layout={renderPreview ? 'stacked' : 'inline'}
            name={name}
            value={option.value}
            checked={option.value === value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
            label={
              <>
                {option.label}
                {option.value === defaultValue && (
                  <span className="ml-2 font-normal text-caption text-gray-600">défaut</span>
                )}
              </>
            }
            description={renderPreview?.(option.value)}
          />
        </div>
      ))}
    </div>
  );
}
